import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Control, useForm } from 'react-hook-form';

import Loader from '@/assets/icons/Loader';
import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { CreatableSelectField } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import { RadioField } from '@/components/UI/Fields/ReactFormFields/RadioField';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { Label } from '@/components/UI/Label/Label';
import { getMutatieErrorMessage, isMutatieSucces } from '@/queries/mutaties/dialogMutatieResponseUtils';
import { useMutaties } from '@/queries/mutaties/useMutaties';
import {
  ActiviteitVoorBewegingModel,
  mapArrayOfActiviteitVoorBeweging,
} from '@/queries/queryModels/activiteitenVoorBewegingModel';
import { mapArrayOfBewegingZonderRit } from '@/queries/queryModels/bewegingZonderRitModel';
import { mapArrayOfInzetPositiesOpTeVersterkenTrein } from '@/queries/queryModels/inzetPositiesOpTeVersterkenTreinModel';
import { mapValidatieVoorEenheidNummer } from '@/queries/queryModels/validatieVoorEenheidNummerModel';
import { mapValidatieVoorTeVersterkenTrein } from '@/queries/queryModels/validatieVoorTeVersterkenTreinModel';
import { useGetQuery } from '@/queries/useGetQuery';
import useClientState from '@/state/useClientState';
import { formatDate } from '@/utils/dateUtils';
import { getDateVariations } from '@/utils/dateVariations';
import { identity } from '@/utils/functions';
import {
  createVersterkOpdrachtData,
  gefilteredActiviteiten,
  mapEnFilterActiviteitOptionsTot,
  mapEnFilterActiviteitOptionsVan,
  validateVersterkDialogVelden,
} from '@/utils/helpers';

import styles from '@/components/UI/Dialog/Dialog.module.css';

export type Inputs = {
  date?: string;
  train?: { treinnummer?: number; dienstregelVersie?: number };
  unit?: string;
  from?: ActiviteitVoorBewegingModel;
  till?: ActiviteitVoorBewegingModel;
  inzetPositie?: string;
};

export type VersterkDialogData = {
  details?: WerklijnContextMenuDetails;
  versterkOpInzet?: boolean;
};

export type VersterkenDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  werklijnGegevens?: VersterkDialogData;
  opInzet: boolean;
} & Inputs;

export const VersterkenDialog = ({
  showDialog,
  handleOpenChange,
  werklijnGegevens,
  train,
  from,
  till,
  opInzet,
}: VersterkenDialogProps) => {
  const [eenheidNummerData, setEenheidNummerData] = useState<{
    materieelSoort?: string;
    materieelAanduiding?: string;
    werklijnId?: string;
    werklijnVersie?: number;
  }>({}); // data voor werklijnGegevens bij versterken op inzet

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const werklijnen = useClientState((state) => state.werklijnen);
  const [warningMessage, setWarningMessage] = useState<boolean>(false);
  const [updatedWerklijnGegevens, setUpdateWerklijnGegevens] = useState<VersterkDialogData | undefined>(
    werklijnGegevens,
  );

  const lockedWerklijnVersie = werklijnGegevens && werklijnGegevens?.details?.werklijnVersie;
  const werklijnVersieFromState = werklijnGegevens?.details?.werklijnId
    ? werklijnen[werklijnGegevens.details.werklijnId]?.versie
    : undefined;

  useEffect(() => {
    const shouldUpdate = werklijnVersieFromState && lockedWerklijnVersie !== werklijnVersieFromState;
    setWarningMessage(!!shouldUpdate);
    if (shouldUpdate && werklijnGegevens?.details) {
      setUpdateWerklijnGegevens({
        ...werklijnGegevens,
        details: {
          ...werklijnGegevens.details,
          werklijnVersie: werklijnVersieFromState,
        },
      });
    }
  }, [lockedWerklijnVersie, werklijnVersieFromState]);

  const { clearErrors, setError, handleSubmit, control, watch, reset, setValue, getValues } = useForm<Inputs>({
    mode: 'onSubmit',
  });

  const getMaterieelType = () => {
    if (updatedWerklijnGegevens?.details?.materieelSoort === 'Onbekend') {
      return `${updatedWerklijnGegevens.details.materieelSoortEersteInzet}`;
    }
    return `${updatedWerklijnGegevens?.details?.eenheidnummer} (${updatedWerklijnGegevens?.details?.materieelSoort}-${updatedWerklijnGegevens?.details?.materieelAanduiding})`;
  };

  const { versterken } = useMutaties();

  const calculateDefaultDate = (updatedWerklijnGegevens, opInzet, dateVariations) => {
    const aankomstTijd = updatedWerklijnGegevens?.details?.aankomstTijd;
    const treinDatum = updatedWerklijnGegevens?.details?.treindatum;
    if (aankomstTijd && !opInzet) {
      const aankomstDateFormatted = format(new Date(aankomstTijd), 'yyyy-MM-dd');
      return dateVariations.find((option) => option.value === aankomstDateFormatted)?.value || dateVariations[0]?.value;
    }
    if (treinDatum && opInzet) return treinDatum;
    return dateVariations[0]?.value;
  };

  const useSelectedDate = (updatedWerklijnGegevens, opInzet, dateVariations) => {
    const [selectedDate, setSelectedDate] = useState('');
    useEffect(() => {
      const newDefaultDate = calculateDefaultDate(updatedWerklijnGegevens, opInzet, dateVariations);
      if (selectedDate !== newDefaultDate) {
        setSelectedDate(newDefaultDate);
      }
    }, [updatedWerklijnGegevens, opInzet, dateVariations]);
    return selectedDate;
  };

  // data voor datum select
  const dateVariations = getDateVariations();
  const selectedDate = useSelectedDate(updatedWerklijnGegevens, opInzet, dateVariations);

  // watch form values
  const watchSelectedDate = watch('date', selectedDate);
  const watchSelectedTrain = watch('train', train);
  const watchActiviteitVan = watch('from', from);
  const watchActiviteitTot = watch('till', till);

  const teVersterkenTreinNumber = `${updatedWerklijnGegevens?.details?.treinnummer} ${
    updatedWerklijnGegevens?.details?.inzetPositie && `- ${updatedWerklijnGegevens.details.inzetPositie}`
  }`;

  const checkSelectedTrainValue =
    typeof watchSelectedTrain === 'string' ? watchSelectedTrain : watchSelectedTrain?.treinnummer;

  // on form submit post data to muteren/versterk api
  const onSubmit = async (formData: Inputs) => {
    const eenheidGegevens = opInzet ? eenheidNummerData : updatedWerklijnGegevens?.details;
    if (validateVersterkDialogVelden(formData, eenheidGegevens)) {
      const versterkOpdracht = createVersterkOpdrachtData(formData, eenheidGegevens);
      const mutatieResponse = await versterken(versterkOpdracht);
      if (isMutatieSucces(mutatieResponse)) {
        handleOpenChange(false);
        setErrorMessage('');
      } else {
        const errorMessage = getMutatieErrorMessage(mutatieResponse);
        if (errorMessage) {
          setErrorMessage(errorMessage);
        }
      }
    }
  };

  // get losse bewegingen - te versterken treinnummers -  voor datum
  const { data: losseBewegingenData, fetchData: fetchLosseBewegingenData } = useGetQuery(mapArrayOfBewegingZonderRit);

  // map losse bewegingen to options in de teVersterkenTrein dropdown
  const teVersterkenTreinen = useMemo(() => {
    return losseBewegingenData?.map((item) => ({
      value: {
        treinnummer: item.treinnummer,
        dienstregelVersie: item.dienstregelVersie,
      },
      label: item.treinnummer.toString(),
    }));
  }, [losseBewegingenData]);

  // get activiteiten voor beweging
  const {
    data: activiteitenVoorBewegingData,
    fetchData: fetchActiviteitenVoorBewegingData,
    clearData: clearActiviteitenVoorBewegingData,
  } = useGetQuery(mapArrayOfActiviteitVoorBeweging);

  // alle activiteiten voor beide van en tot dropdowns
  const activiteitenOptions = useMemo(() => {
    if (
      activiteitenVoorBewegingData !== null &&
      activiteitenVoorBewegingData !== undefined &&
      updatedWerklijnGegevens?.details !== undefined
    ) {
      return opInzet
        ? gefilteredActiviteiten(activiteitenVoorBewegingData, updatedWerklijnGegevens.details, true)
        : activiteitenVoorBewegingData;
    }
  }, [activiteitenVoorBewegingData, opInzet, updatedWerklijnGegevens?.details]);

  // map activiteitenVoorBeweging naar de opties in de Van-dropdown
  const activiteitenOptionsVan = useMemo(() => {
    return mapEnFilterActiviteitOptionsVan(activiteitenOptions);
  }, [activiteitenOptions]);

  // map activiteiten van OptionsVan to options in Tot-dropdown -- indexVan + 1 omdat we de geselecteerde optie in de Van dropdown niet willen tonen in de Tot dropdown
  const activiteitenOptionsTot = useMemo(() => {
    return mapEnFilterActiviteitOptionsTot(activiteitenOptions, watchActiviteitVan);
  }, [activiteitenOptions, watchActiviteitVan]);

  // get alle eenheidnummers - opInzet
  const { data: alleEenheidNummersData, fetchData: fetchAlleEenheidNummersData } = useGetQuery(identity);

  // map alle eenheidnummers to options in de teVersterkenTrein dropdown
  const teVersterkenEenheidNummers = useMemo(() => {
    return alleEenheidNummersData?.map((item) => ({
      value: item,
      label: item,
    }));
  }, [alleEenheidNummersData]);

  // valideer eenheidnummer
  const { data: validatieOpEenheidNummerData, fetchData: fetchValidatieOpEenheidNummerData } =
    useGetQuery(mapValidatieVoorEenheidNummer);

  // valideer teVersterkenTrein
  const { data: validatieOpTeVersterkenTreinData, fetchData: fetchValidatieOpTeVersterkenTreinData } = useGetQuery(
    mapValidatieVoorTeVersterkenTrein,
  );

  // get inzetPosities voor te versterken trein
  const {
    data: inzetPositieData,
    fetchData: fetchInzetPositieData,
    clearData: clearInzetPositieData,
  } = useGetQuery(mapArrayOfInzetPositiesOpTeVersterkenTrein);

  // map inzetPositieData naar de opties in de inzetPositie dropdown
  const inzetPosities = useMemo(() => {
    if (inzetPositieData !== undefined) {
      setLoading(false);
    }
    return inzetPositieData?.map((item) => ({
      value: item.positie.toString(),
      label: item.volledigeNaam,
    }));
  }, [inzetPositieData]);

  // reset all values
  const resetDialog = (closeButtonCLick: boolean) => {
    if (closeButtonCLick) {
      handleOpenChange(false);
    }
    reset();
    clearActiviteitenVoorBewegingData();
    clearInzetPositieData();
  };

  // fetch losse bewegingen als er een datum is geselecteerd
  // reset teVersterkenTrein en inzetPositie  als er van datum wordt gewisseld
  const handleDateChange = async (date) => {
    // fetch losse bewegingen alleen bij INITIEEL_VERSTERKEN
    !opInzet && (await fetchLosseBewegingenData(`/bewegingen/bewegingenzonderrit/${date}`));
    // fetch validatieOpTeVersterkenTreinData alleen bij opInzet
    !!opInzet &&
      fetchValidatieOpTeVersterkenTreinData(
        `/bewegingen/validatie/${updatedWerklijnGegevens?.details?.treinnummer}/${date}`,
      );
    // fetchAlleEenheidNummersData alleen opInzet
    !!opInzet && (await fetchAlleEenheidNummersData('/stamgegevens/eenheidnummers'));
    setValue('date', date);
    setValue('from', undefined);
    setValue('train', undefined);
    setValue('unit', undefined);
    clearActiviteitenVoorBewegingData();
    clearInzetPositieData();
    clearErrors();
  };

  // clear formfields en valideer teVersterkenTrein als er een treinnummer is geselecteerd
  // Alleen bij INITIEEL_VERSTERKEN
  const handleTrainNumberChange = async (e: string | { treinnummer: number; dienstregelVersie: number } | null) => {
    setValue('from', undefined);
    clearInzetPositieData();
    clearActiviteitenVoorBewegingData();
    const treinnummer = typeof e === 'string' ? e : e?.treinnummer.toString();
    await fetchValidatieOpTeVersterkenTreinData(`/bewegingen/validatie/${treinnummer}/${watchSelectedDate}`);
  };

  // clear fromfields en valideer eenheidnummer als er een eenheidnummer is geselecteerd
  const handleUnitNumberChange = async (unitNumber: string) => {
    setValue('from', undefined);
    clearInzetPositieData();
    clearActiviteitenVoorBewegingData();
    await fetchValidatieOpEenheidNummerData(`/werklijnen/versterk-extract/${unitNumber}`);
  };

  // reset dialog als dialog gesloten wordt via een outsideClick
  useEffect(() => {
    if (!showDialog) {
      resetDialog(false);
    } else if (showDialog) {
      clearErrors();
      const defaultDate = calculateDefaultDate(updatedWerklijnGegevens, opInzet, dateVariations);
      // fetch losse bewegingen alleen bij openen dialoog INITIEEL_VERSTERKEN
      !opInzet && fetchLosseBewegingenData(`/bewegingen/bewegingenzonderrit/${defaultDate}`);
      // fetchAlleEenheidNummersData alleen bij openen dialoog opInzet
      opInzet && fetchAlleEenheidNummersData('/stamgegevens/eenheidnummers');
      // fetch validatieOpTeVersterkenTreinData alleen bij openen dialoog opInzet
      opInzet &&
        fetchValidatieOpTeVersterkenTreinData(
          `/bewegingen/validatie/${updatedWerklijnGegevens?.details?.treinnummer}/${defaultDate}`,
        );
      // set default value for date select when dialog opens
      setValue('date', defaultDate);
    }
    // only reset when showDialog changes
  }, [showDialog]);

  // validate teVersterkenTrein and set error if invalid
  useEffect(() => {
    if (validatieOpTeVersterkenTreinData) {
      clearErrors();
      setValue('train', {
        treinnummer: validatieOpTeVersterkenTreinData?.treinnummer,
        dienstregelVersie: validatieOpTeVersterkenTreinData?.dienstregelVersie,
      });
      // alleen bij INITIEEL_VERSTERKEN
      // fetch activiteiten voor beweging na validatie (is null bij invalid response)
      !opInzet &&
        fetchActiviteitenVoorBewegingData(
          `/bewegingen/activiteiten/${validatieOpTeVersterkenTreinData?.treinnummer}/${watchSelectedDate}`,
        );
    } else if (checkSelectedTrainValue) {
      // Niet foutmeldingen geven wanneer er niet een trein is geselecteerd
      clearActiviteitenVoorBewegingData();
      setError('train', {
        type: 'manual',
        message: 'Trein bestaat niet op geselecteerde dag',
      });
    }

    // only run when validatieOpTeVersterkenTreinResponse is fetched
  }, [validatieOpTeVersterkenTreinData]);

  // alleen bij opInzet
  // validate eenheidnummer and map eenheidnummer to werklijnGegevens
  useEffect(() => {
    if (validatieOpEenheidNummerData) {
      clearErrors();
      !!opInzet &&
        updatedWerklijnGegevens?.details?.treinnummer &&
        fetchActiviteitenVoorBewegingData(
          `/bewegingen/activiteiten/${updatedWerklijnGegevens?.details?.treinnummer}/${watchSelectedDate}`,
        );
      setEenheidNummerData({
        materieelSoort: validatieOpEenheidNummerData?.eenheidSoort,
        materieelAanduiding: validatieOpEenheidNummerData?.eenheidAanduiding,
        werklijnId: validatieOpEenheidNummerData?.betrokkenWerklijn?.awbId,
        werklijnVersie: validatieOpEenheidNummerData?.betrokkenWerklijn?.versie,
      });
    } else if (validatieOpEenheidNummerData === null && getValues('unit')) {
      setError('unit', {
        type: 'manual',
        message: 'Dit materieelnummer bestaat niet',
      });
      clearActiviteitenVoorBewegingData();
    } else if (validatieOpEenheidNummerData === undefined) {
      clearErrors();
    }

    // only run when validatieOpEenheid is fetched
  }, [clearErrors, validatieOpEenheidNummerData]);

  // set default values for Van- and Tot-dropdown
  useEffect(() => {
    if (activiteitenOptionsVan) {
      // set default values voor Van- en Tot-dropdown
      setValue('from', activiteitenOptionsVan?.[0]?.value);
    }
    if (activiteitenOptionsTot) {
      setValue('till', activiteitenOptionsTot[activiteitenOptionsTot.length - 1].value);
    }

    // only run when activiteiten are fetched from api
  }, [activiteitenOptions]);

  // fetch inzetPosities als dienstregelpunt van is geselecteerd
  useEffect(() => {
    setLoading(true);
    if (watchActiviteitVan) {
      const treinNummer = opInzet ? updatedWerklijnGegevens?.details?.treinnummer : checkSelectedTrainValue;
      fetchInzetPositieData(
        `/inzet/bepaal-posities/${treinNummer}/${watchSelectedDate}/${watchActiviteitVan.dienstregelpunt}`,
      );
    }
    // only run when watchActiviteitVan changes
  }, [watchActiviteitVan]);

  // Wanneer de value de Van-dropdown groter is dan de value van de Tot-dropdown, set de value van de Tot-dropdown op de laatste optie in de Van-dropdown
  useEffect(() => {
    if (watchActiviteitTot && watchActiviteitVan && watchActiviteitVan?.volgnummer >= watchActiviteitTot?.volgnummer) {
      setValue('till', activiteitenOptionsTot?.[activiteitenOptionsTot?.length - 1].value);
    } else if (watchActiviteitVan?.volgnummer === watchActiviteitTot?.volgnummer) {
      setValue('till', undefined);
    }
    // only run when watchActiviteitTot or watchActiviteitVan changes
  }, [watchActiviteitVan, watchActiviteitTot]);

  // De inzetPositie-waarde altijd wordt ingesteld wanneer er beschikbare opties zijn, ongeacht hoe vaak de gebruiker een andere trein selecteert.
  // Als er geen inzetPosities beschikbaar zijn, wordt de inzetPositie-waarde in het formulier gewist.
  useEffect(() => {
    if (inzetPosities && inzetPosities.length > 0) {
      setValue('inzetPositie', inzetPosities[0]?.value);
    } else {
      setValue('inzetPositie', undefined);
    }
    // only run when inzetPosities changes
  }, [inzetPosities, setValue]);

  return (
    <MwdDialog
      title='Versterken'
      className={styles.versterkenDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid={`contextMenu-${updatedWerklijnGegevens?.details?.treinnummer}-${updatedWerklijnGegevens?.details?.inzetPositie}`}
      hasCloseInHeader={true}
      // error message has priority over warning message
      showNotification={!!errorMessage || warningMessage}
      notificationType={errorMessage ? 'error' : 'warning'}
      notificationText={errorMessage || 'Let op: de werklijnversie is gewijzigd'}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {opInzet ? (
          <div>
            <Label>Datum</Label>
            <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>
              {formatDate(new Date(updatedWerklijnGegevens?.details?.treindatum ?? ''))}
            </div>
            <Label>Treinnummer</Label>
            <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>
              {teVersterkenTreinNumber}
            </div>
            <CreatableSelectField
              placeholder='Selecteer materieel...'
              createLabel='Zoek materieel:'
              onChangeCallback={(e) => handleUnitNumberChange(e as string)}
              control={control as Control<any>}
              testId='unit-select'
              required
              matchFromStart
              name='unit'
              label='Eenheid'
              options={teVersterkenEenheidNummers}
            />
          </div>
        ) : (
          <>
            <SelectField
              placeholder='Selecteer een datum...'
              control={control}
              onChangeCallback={(e) => handleDateChange(e)}
              required
              testId='date-select'
              name='date'
              label='Datum'
              options={dateVariations}
              defaultValue={selectedDate}
            />
            <CreatableSelectField
              placeholder='Selecteer een trein...'
              createLabel='Zoek trein met inzet:'
              control={control as Control<any>}
              matchFromStart
              onChangeCallback={(e) =>
                handleTrainNumberChange(e as string | { treinnummer: number; dienstregelVersie: number } | null)
              }
              testId='train-select'
              inputId='versterkenDialogTrainSelect'
              required
              name='train'
              label='Treinnummer'
              options={teVersterkenTreinen}
            />

            <Label>Eenheid</Label>
            <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>
              {getMaterieelType()}
            </div>
          </>
        )}
        <div
          style={{
            backgroundColor: 'var(--tractie-color-border-subtle)',
            height: '2px',
            width: '100%',
            marginBottom: '14px',
            marginTop: '14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            gap: '16px',
          }}
        >
          <SelectField
            placeholder='Van...'
            control={control}
            testId='van-select'
            required
            name='from'
            label='Van'
            options={activiteitenOptionsVan}
          />
          <SelectField
            placeholder='Tot...'
            control={control}
            testId='tot-select'
            required
            name='till'
            label='Tot'
            options={activiteitenOptionsTot}
          />
        </div>
        {/* Van punt is geselecteer en inzetposities worden geladen */}
        {loading && activiteitenOptionsVan && (
          <>
            <Label>Inzetpositie</Label>
            <div>
              <Loader className={styles.loadingIcon} fill='var(--tractie-color-content-emphasis)' />
            </div>
          </>
        )}
        {!loading && inzetPosities && (
          <>
            <div
              style={{
                display: inzetPosities.length > 1 ? 'block' : 'none',
              }}
            >
              <RadioField
                name='inzetPositie'
                defaultValue={inzetPosities[0]?.value}
                label='Inzetpositie'
                required
                control={control}
                options={inzetPosities}
                rules={{
                  validate: {
                    inzetPositie: (value) => !!value || 'Dit veld is verplicht',
                  },
                }}
              />
            </div>
            <div
              style={{
                display: inzetPosities.length === 1 ? 'block' : 'none',
              }}
            >
              <Label>Inzetpositie</Label>
              <div style={{ color: 'var(--tractie-color-content-default)', paddingLeft: '8px' }}>
                {inzetPosities[0]?.label}
              </div>
            </div>
          </>
        )}
        <MwdDialog.Footer submitCaption='Versterk' cancelCallback={() => resetDialog(true)} />
      </form>
    </MwdDialog>
  );
};
